import * as React from 'react';
import * as I18n from 'i18n-js';
import FormGroupWithInput from '@reverbdotcom/commons/dist/components/form_group_with_input';
import FormGroupWithSelect from '@reverbdotcom/commons/dist/components/form_group_with_select';
import {
  ITokenizedCreditCardData,
  IBillingCardAddress as BillingCardAddress,
} from '../credit_cards/card_data';
import TokenizedCreditCardFields from '../credit_cards/tokenized_credit_card_fields';
import { core_apimessages_Country as Country } from '../schemas/rql';
import CreditCardEncryptedInput from './credit_card_encrypted_input';
import CreditCardEncryptedDateInput from './credit_card_encrypted_date_input';
import { AdyenCardType } from '../lib/adyen';
import { useAdyenCard } from './adyen_card_hook';
import { useExpEnabled } from '@reverbdotcom/commons/dist/user_hooks';
import { experiments } from '@reverbdotcom/commons/dist/constants';

export interface Props {
  setCardholderName(cardholderName: string): void;
  setCardState(data: Partial<ITokenizedCreditCardData>): void;
  setBillingCardAddress(data: Partial<BillingCardAddress>): void;
  cardholderName: string;
  billingCardAddress: BillingCardAddress;
  countries: Country[];
}

function countryOptions(countries: Country[]) {
  return countries.map((country) => ({
    value: country.countryCode,
    text: country.name,
  }));
}

export default function BillingMethodNewCardFormFields(props: Props) {
  return (
    <div>
      <FormGroupWithInput
        inputName="cardholderName"
        label={I18n.t('discovery.creditCardForm.nameOnCard')}
        value={props.cardholderName}
        updateField={(field) => props.setCardholderName(field.cardholderName)}
        fieldName="cardholderName"
        required
      />
      <RolloutCardFormFields setCardState={props.setCardState} />
      <div className="g-container">
        <div className="g-col-6 g-col-mobile-12 scaling-mb-2">
          <FormGroupWithInput
            label={I18n.t('discovery.creditCardForm.postalCode')}
            fieldName="postalCode"
            inputName="billingAddressPostalCode"
            value={props.billingCardAddress.postalCode}
            updateField={props.setBillingCardAddress}
            required
          />
        </div>
        <div className="g-col-6 g-col-mobile-12 scaling-mb-2">
          <FormGroupWithSelect
            label={I18n.t('discovery.creditCardForm.country')}
            fieldName="countryCode"
            inputName="billingAddressCountryCode"
            updateField={props.setBillingCardAddress}
            value={props.billingCardAddress.countryCode}
            required
            options={countryOptions(props.countries)}
          />
        </div>
      </div>
    </div>
  );
}

function RolloutCardFormFields({ setCardState }: { setCardState(data: Partial<ITokenizedCreditCardData>): void; }) {
  if (useExpEnabled(experiments.ADYEN_WEB_COMPONENTS_UPDATE)) {
    return <AddNewCardFormFields setCardState={setCardState} />;
  }

  return (<TokenizedCreditCardFields onTokenizedFieldUpdate={setCardState} />);
}

function AddNewCardFormFields({ setCardState }: { setCardState(data: Partial<ITokenizedCreditCardData>): void; }) {
  const { cardState, focus, errors } = useAdyenCard({
    selector: '[data-card-fields]',
    cardType: AdyenCardType.Card,
  });

  const { paymentMethod } = cardState.data;

  React.useEffect(() => {
    setCardState(cardState.data.paymentMethod);
  }, [cardState, setCardState]);

  return (
    <div data-card-fields className="g-container">
      <div className="g-col-12 scaling-mb-2">
        <CreditCardEncryptedInput
          inputName="cardNumber"
          inputValue={paymentMethod.encryptedCardNumber}
          cseName="encryptedCardNumber"
          label={I18n.t('discovery.creditCardForm.cardNumber')}
          errors={errors}
          focus={focus}
        />
      </div>
      <div className="g-col-6 g-col-mobile-12 scaling-mb-2">
        <CreditCardEncryptedDateInput
          expiryMonthInputName="expiryMonth"
          expiryYearInputName="expiryYear"
          paymentMethod={paymentMethod}
          label={I18n.t('discovery.creditCardForm.expiration')}
          errors={errors}
          focus={focus}
        />
      </div>
      <div className="g-col-6 g-col-mobile-12 scaling-mb-2">
        <CreditCardEncryptedInput
          inputName="securityCode"
          inputValue={paymentMethod.encryptedSecurityCode}
          label={I18n.t('discovery.creditCardForm.cvv')}
          cseName="encryptedSecurityCode"
          errors={errors}
          focus={focus}
        />
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/components/billing_method_new_card_form_fields.tsx